import { db } from '../lib/firebase/config';
import { collection, doc, getDocs, query, where, updateDoc, addDoc, orderBy, limit, Timestamp, setDoc, deleteDoc } from 'firebase/firestore';
import { PhotoLogItem, PhotoLogMonth, PhotoLogStatus } from '../lib/types/photo-log';
import { format } from 'date-fns';

const COLLECTION_NAME = 'photo_log';

export interface SkuMapping {
  skuBase: string;
  pmBase: string;
}

export const SKU_MAPPINGS: SkuMapping[] = [
  // --- Most specific first (longest prefixes) ---
  { skuBase: '2001011', pmBase: 'PM2011' },
  { skuBase: '2001012', pmBase: 'PM2012' },

  // --- Likely active ranges (2001010–2001019) ---
  { skuBase: '2001019', pmBase: 'PM2019' },
  { skuBase: '2001018', pmBase: 'PM2018' },
  { skuBase: '2001017', pmBase: 'PM2017' },
  { skuBase: '2001016', pmBase: 'PM2016' },
  { skuBase: '2001015', pmBase: 'PM2015' },
  { skuBase: '2001014', pmBase: 'PM2014' },
  { skuBase: '2001013', pmBase: 'PM2013' },
  { skuBase: '2001010', pmBase: 'PM2010' },

  // --- Other likely-used ranges ---
  { skuBase: '200102', pmBase: 'PM202' },
  { skuBase: '200103', pmBase: 'PM203' },

  { skuBase: '200101', pmBase: 'PM201' },
  { skuBase: '200100', pmBase: 'PM200' },
  { skuBase: '200000', pmBase: 'PM100' },
  { skuBase: '200200', pmBase: 'PM300' },

  // --- Expanding on 2003 series ---
  { skuBase: '2003011', pmBase: 'PM4011' },
  { skuBase: '2003010', pmBase: 'PM4010' },
  { skuBase: '200301',  pmBase: 'PM401' },
  { skuBase: '200300',  pmBase: 'PM400' },
  { skuBase: '2003',    pmBase: 'PM4' },

  // --- Other known series ---
  { skuBase: '200500', pmBase: 'PM600' },
];

export const photoLogService = {
  /**
   * Reverse of convertBarcodeToSku: turns a PM-format SKU back into a barcode.
   * e.g. PM2028425 → 2001028425  (using mapping PM202 ↔ 200102)
   */
  convertSkuToBarcode(sku: string): string {
    if (!sku || typeof sku !== 'string') throw new Error('Invalid SKU');
    const upper = sku.toUpperCase();
    // Sort mappings by pmBase length descending so the longest prefix matches first
    const sorted = [...SKU_MAPPINGS].sort((a, b) => b.pmBase.length - a.pmBase.length);
    const mapping = sorted.find(m => upper.startsWith(m.pmBase));
    if (!mapping) throw new Error(`No barcode mapping for SKU: ${sku}`);
    const remaining = upper.slice(mapping.pmBase.length);
    if (!/^\d+$/.test(remaining)) throw new Error(`Non-numeric suffix: ${remaining}`);
    return `${mapping.skuBase}${remaining}`;
  },

  convertBarcodeToSku(barcode: string): string {
    console.log('Converting barcode to SKU:', barcode);
    
    // Ensure barcode is a string and has correct length
    if (!barcode || typeof barcode !== 'string') {
      throw new Error('Invalid barcode: must be a non-empty string');
    }

    if (barcode.length < 7) {
      throw new Error(`Invalid barcode length: ${barcode.length} (must be at least 7 characters)`);
    }

    // Find matching base
    const mapping = SKU_MAPPINGS.find(m => barcode.startsWith(m.skuBase));
    if (!mapping) {
      console.error('SKU mapping failed for barcode:', {
        barcode,
        availableMappings: SKU_MAPPINGS.map(m => ({ 
          skuBase: m.skuBase, 
          pmBase: m.pmBase,
          matches: barcode.startsWith(m.skuBase)
        }))
      });
      throw new Error(`No matching SKU base found for barcode: ${barcode}. Valid bases are: ${SKU_MAPPINGS.map(m => m.skuBase).join(', ')}`);
    }

    // Get the remaining digits after the base
    const remainingDigits = barcode.slice(mapping.skuBase.length);
    
    // Validate remaining digits
    if (!/^\d+$/.test(remainingDigits)) {
      throw new Error(`Invalid remaining digits: ${remainingDigits} (must be numeric)`);
    }

    // Combine PM base with remaining digits
    const sku = `${mapping.pmBase}${remainingDigits}`;
    console.log('Successfully converted barcode to SKU:', { 
      barcode, 
      sku,
      mapping: {
        skuBase: mapping.skuBase,
        pmBase: mapping.pmBase,
        remainingDigits
      }
    });
    return sku;
  },

  async addItem(item: Omit<PhotoLogItem, 'id'>): Promise<PhotoLogItem> {
    try {
      console.log('Starting addItem process with:', item);

      // Validate required fields
      if (!item.barcode) throw new Error('Missing required field: barcode');
      if (!item.sku) throw new Error('Missing required field: sku');
      if (!item.month) throw new Error('Missing required field: month');
      if (!item.createdBy) throw new Error('Missing required field: createdBy');
      if (!item.lastModifiedBy) throw new Error('Missing required field: lastModifiedBy');
      if (!item.datePhotographed) throw new Error('Missing required field: datePhotographed');
      if (!item.status) throw new Error('Missing required field: status');

      // Validate month format
      const monthRegex = /^\d{4}-\d{2}$/;
      if (!monthRegex.test(item.month)) {
        throw new Error(`Invalid month format: ${item.month} (must be YYYY-MM)`);
      }

      // Prepare the item data
      const now = Timestamp.now();
      const itemData = {
        barcode: item.barcode,
        sku: item.sku,
        datePhotographed: item.datePhotographed,
        status: item.status,
        month: item.month,
        createdBy: item.createdBy,
        lastModifiedBy: item.lastModifiedBy,
        createdAt: now,
        lastModifiedAt: now
      };

      console.log('Prepared item data:', itemData);

      try {
        // Create the month document first
        const monthDocRef = doc(db, COLLECTION_NAME, item.month);
        await setDoc(monthDocRef, { 
          month: item.month,
          createdAt: now,
          updatedAt: now
        }, { merge: true });

        console.log('Month document created/updated:', item.month);
      } catch (error) {
        console.error('Error creating month document:', error);
        throw new Error('Failed to create month document');
      }

      try {
        // Add the item to the entries subcollection
        const entriesRef = collection(db, COLLECTION_NAME, item.month, 'entries');
        const docRef = await addDoc(entriesRef, itemData);
        console.log('Successfully added item with ID:', docRef.id);

        // Return the complete item with ID
        const savedItem = { id: docRef.id, ...itemData };
        console.log('Returning saved item:', savedItem);
        return savedItem;
      } catch (error) {
        console.error('Error adding item to entries:', error);
        throw new Error('Failed to add item to entries collection');
      }
    } catch (error) {
      console.error('Error in addItem:', error);
      if (error instanceof Error) {
        console.error('Error details:', {
          name: error.name,
          message: error.message,
          stack: error.stack
        });
        throw new Error(`Failed to add item: ${error.message}`);
      }
      throw new Error('Failed to add item: Unknown error');
    }
  },

  async updateItemStatus(id: string, status: PhotoLogStatus, userId: string, month: string): Promise<void> {
    try {
      console.log('Updating item status:', { id, status, userId, month });
      const docRef = doc(db, COLLECTION_NAME, month, 'entries', id);
      const now = Timestamp.now();
      
      // Build update object
      const updateData: Record<string, any> = {
        status,
        lastModifiedBy: userId,
        lastModifiedAt: now
      };
      
      // Track when status changed to 'edited' specifically
      if (status === 'edited') {
        updateData.editedAt = now;
      }
      
      await updateDoc(docRef, updateData);
      console.log('Successfully updated item status');
    } catch (error) {
      console.error('Error updating item status:', error);
      if (error instanceof Error) {
        console.error('Error details:', {
          message: error.message,
          stack: error.stack
        });
      }
      throw error;
    }
  },

  async getItemsByMonth(month: string): Promise<PhotoLogItem[]> {
    try {
      const monthRef = collection(db, COLLECTION_NAME, month, 'entries');
      const q = query(monthRef, orderBy('datePhotographed', 'desc'));

      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as PhotoLogItem));
    } catch (error) {
      console.error('Error getting items by month:', error);
      if (error instanceof Error) {
        console.error('Error details:', {
          message: error.message,
          stack: error.stack
        });
      }
      throw error;
    }
  },

  async searchByBarcode(searchQuery: string): Promise<PhotoLogItem[]> {
    try {
      console.log('Searching for:', searchQuery);
      
      // Get all months in the last year
      const months: string[] = [];
      const currentDate = new Date();
      for (let i = 0; i < 12; i++) {
        const date = new Date(currentDate);
        date.setMonth(date.getMonth() - i);
        months.push(format(date, 'yyyy-MM'));
      }

      // Search in each month's subcollection
      const allItems: PhotoLogItem[] = [];
      const searchQueryLower = searchQuery.toLowerCase();

      for (const month of months) {
        const monthRef = collection(db, COLLECTION_NAME, month, 'entries');
        // Get all items from the month and filter client-side for more flexible searching
        const snapshot = await getDocs(monthRef);
        const items = snapshot.docs
          .map(doc => ({ id: doc.id, ...doc.data() } as PhotoLogItem))
          .filter(item => 
            item.barcode.toLowerCase().includes(searchQueryLower) ||
            item.sku.toLowerCase().includes(searchQueryLower)
          );
        allItems.push(...items);
      }

      console.log(`Found ${allItems.length} items matching "${searchQuery}":`, allItems);
      return allItems;
    } catch (error) {
      console.error('Error searching items:', error);
      if (error instanceof Error) {
        console.error('Error details:', {
          message: error.message,
          stack: error.stack
        });
      }
      throw error;
    }
  },

    async getMonthStats(month: string): Promise<PhotoLogMonth> {
    const items = await this.getItemsByMonth(month);
    return {
      id: month,
      month,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
      items,
      totalItems: items.length,
      unPhotodCount: items.filter(item => item.status === 'un-photod').length,
      photodCount: items.filter(item => item.status === 'photod').length,
      editedCount: items.filter(item => item.status === 'edited').length,
    };
  },

  async deleteItem(month: string, itemId: string): Promise<void> {
    try {
      console.log('Deleting item:', { month, itemId });
      const docRef = doc(db, COLLECTION_NAME, month, 'entries', itemId);
      await deleteDoc(docRef);
      console.log('Successfully deleted item');
    } catch (error) {
      console.error('Error deleting item:', error);
      throw new Error('Failed to delete item');
    }
  },

  async updateItem(month: string, itemId: string, updates: Partial<PhotoLogItem>): Promise<void> {
    try {
      console.log('Updating item:', { month, itemId, updates });
      const docRef = doc(db, COLLECTION_NAME, month, 'entries', itemId);
      await updateDoc(docRef, {
        ...updates,
        updatedAt: Timestamp.now()
      });
      console.log('Successfully updated item');
    } catch (error) {
      console.error('Error updating item:', error);
      throw new Error('Failed to update item');
    }
  },

  /**
   * SKU → photo status for the last 24 months. Fetched once per session so the
   * inventory database does not re-download ~2 MB every time a tab remounts.
   */
  getStatusLookup(): Promise<Record<string, PhotoLogStatus>> {
    if (photoStatusLookup) return Promise.resolve(photoStatusLookup);
    if (!photoStatusInflight) {
      photoStatusInflight = (async () => {
        const now = new Date();
        const months: string[] = [];
        for (let i = 0; i < 24; i++) {
          const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
          months.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
        }
        const all = (
          await Promise.all(
            months.map((month) => this.getItemsByMonth(month).catch(() => [] as PhotoLogItem[])),
          )
        ).flat();
        const lookup: Record<string, PhotoLogStatus> = {};
        for (const entry of all) {
          const status = entry.status as PhotoLogStatus;
          if (entry.sku) lookup[entry.sku.trim().toUpperCase()] = status;
          if (entry.barcode) lookup[entry.barcode.trim().toUpperCase()] = status;
        }
        photoStatusLookup = lookup;
        return lookup;
      })();
    }
    return photoStatusInflight;
  },
};

let photoStatusLookup: Record<string, PhotoLogStatus> | null = null;
let photoStatusInflight: Promise<Record<string, PhotoLogStatus>> | null = null; 
